import React from "react";
import {
  Utensils,
  ChefHat,
  Refrigerator,
  Microwave,
  Flame,
  Coffee,
  Soup,
  Droplets,
  Box,
  Scale,
  Scissors,
  ShoppingBasket,
  GlassWater,
  Plug,
  ShieldCheck,
} from "lucide-react";

/* ================= TYPES ================= */
export interface CategoryItem {
  id: number;
  name: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  parentId?: number | null;
  color?: string;
  image?: any;
}

/* ──────────────────────────────────────────────
   1️⃣ TOP-LEVEL KITCHEN CATEGORIES (400000xx)
   Every category below is now its own standalone
   item — nothing is combined with "&" anymore.
   ────────────────────────────────────────────── */
export const kitchenCategories: CategoryItem[] = [
  { id: 40000001, name: "Cookware", icon: Soup, color: "#D84315" },
  { id: 40000002, name: "Bakeware", icon: Flame, color: "#BF360C" },
  { id: 40000003, name: "Kitchen Appliances", icon: Plug, color: "#6A1B9A" },
  { id: 40000004, name: "Kitchen Tools", icon: Utensils, color: "#2E7D32" },
  { id: 40000011, name: "Utensils", icon: Utensils, color: "#2E7D32" },
  { id: 40000005, name: "Food Storage", icon: Box, color: "#455A64" },
  { id: 40000006, name: "Drinkware", icon: GlassWater, color: "#0277BD" },
  { id: 40000007, name: "Tableware", icon: ShoppingBasket, color: "#8D6E63" },
  { id: 40000012, name: "Serving", icon: ShoppingBasket, color: "#8D6E63" },
  { id: 40000008, name: "Kitchen Organization", icon: Box, color: "#5D4037" },
  { id: 40000009, name: "Cleaning", icon: Droplets, color: "#0288D1" },
  { id: 40000013, name: "Dishwashing", icon: Droplets, color: "#0288D1" },
  { id: 40000010, name: "Kitchen Safety", icon: ShieldCheck, color: "#2E7D32" },
  { id: 40000014, name: "Kitchen Accessories", icon: ShieldCheck, color: "#2E7D32" },
];

/* ──────────────────────────────────────────────
   2️⃣ KITCHEN SUBCATEGORIES (40000xxx / 40001xxx)
   ────────────────────────────────────────────── */
export const kitchenSubcategories: CategoryItem[] = [
  /* Cookware */
  { id: 40000101, parentId: 40000001, name: "Pots", icon: Soup },
  { id: 40000105, parentId: 40000001, name: "Pans", icon: Soup },
  { id: 40000102, parentId: 40000001, name: "Pressure Cookers", icon: Flame },
  { id: 40000103, parentId: 40000001, name: "Non-Stick Cookware", icon: Soup },
  { id: 40000104, parentId: 40000001, name: "Cast Iron", icon: Soup },
  { id: 40000106, parentId: 40000001, name: "Stainless Steel", icon: Soup },
  { id: 40000107, parentId: 40000001, name: "Cookware Sets", icon: Soup },

  /* Bakeware */
  { id: 40000201, parentId: 40000002, name: "Baking Trays", icon: Flame },
  { id: 40000204, parentId: 40000002, name: "Baking Pans", icon: Flame },
  { id: 40000202, parentId: 40000002, name: "Cake Molds", icon: Flame },
  { id: 40000205, parentId: 40000002, name: "Muffin Molds", icon: Flame },
  { id: 40000203, parentId: 40000002, name: "Oven Accessories", icon: Flame },
  { id: 40000206, parentId: 40000002, name: "Bakeware Sets", icon: Flame },

  /* Appliances */
  { id: 40000301, parentId: 40000003, name: "Microwaves", icon: Microwave },
  { id: 40000302, parentId: 40000003, name: "Blenders", icon: Utensils },
  { id: 40000306, parentId: 40000003, name: "Mixers", icon: Utensils },
  { id: 40000303, parentId: 40000003, name: "Coffee Makers", icon: Coffee },
  { id: 40000307, parentId: 40000003, name: "Tea Makers", icon: Coffee },
  { id: 40000304, parentId: 40000003, name: "Air Fryers", icon: Flame },
  { id: 40000308, parentId: 40000003, name: "Ovens", icon: Flame },
  { id: 40000305, parentId: 40000003, name: "Refrigerators", icon: Refrigerator },
  { id: 40000309, parentId: 40000003, name: "Freezers", icon: Refrigerator },

  /* Kitchen Tools */
  { id: 40000401, parentId: 40000004, name: "Knives", icon: Scissors },
  { id: 40000405, parentId: 40000004, name: "Cutting Tools", icon: Scissors },
  { id: 40000403, parentId: 40000004, name: "Measuring Tools", icon: Scale },
  { id: 40000404, parentId: 40000004, name: "Graters", icon: ChefHat },
  { id: 40000406, parentId: 40000004, name: "Peelers", icon: ChefHat },

  /* Utensils */
  { id: 40001101, parentId: 40000011, name: "Cooking Utensils", icon: Utensils },

  /* Food Storage */
  { id: 40000501, parentId: 40000005, name: "Food Containers", icon: Box },
  { id: 40000502, parentId: 40000005, name: "Lunch Boxes", icon: Box },
  { id: 40000503, parentId: 40000005, name: "Vacuum Storage", icon: Box },

  /* Drinkware */
  { id: 40000601, parentId: 40000006, name: "Glasses", icon: GlassWater },
  { id: 40000604, parentId: 40000006, name: "Tumblers", icon: GlassWater },
  { id: 40000602, parentId: 40000006, name: "Mugs", icon: Coffee },
  { id: 40000605, parentId: 40000006, name: "Cups", icon: Coffee },
  { id: 40000603, parentId: 40000006, name: "Water Bottles", icon: GlassWater },

  /* Tableware */
  { id: 40000701, parentId: 40000007, name: "Plates", icon: ShoppingBasket },
  { id: 40000704, parentId: 40000007, name: "Bowls", icon: ShoppingBasket },
  { id: 40000702, parentId: 40000007, name: "Cutlery Sets", icon: Utensils },
  { id: 40000705, parentId: 40000007, name: "Dinnerware Sets", icon: ShoppingBasket },

  /* Serving */
  { id: 40001201, parentId: 40000012, name: "Serving Dishes", icon: ShoppingBasket },
  { id: 40001202, parentId: 40000012, name: "Trays", icon: ShoppingBasket },

  /* Organization */
  { id: 40000801, parentId: 40000008, name: "Drawer Organizers", icon: Box },
  { id: 40000802, parentId: 40000008, name: "Spice Racks", icon: Box },
  { id: 40000803, parentId: 40000008, name: "Pantry Storage", icon: Box },

  /* Cleaning */
  { id: 40000901, parentId: 40000009, name: "Dish Racks", icon: Droplets },
  { id: 40000902, parentId: 40000009, name: "Cleaning Tools", icon: Droplets },

  /* Dishwashing */
  { id: 40001301, parentId: 40000013, name: "Dishwashing Accessories", icon: Droplets },

  /* Kitchen Safety */
  { id: 40001001, parentId: 40000010, name: "Gloves", icon: ShieldCheck },
  { id: 40001003, parentId: 40000010, name: "Aprons", icon: ShieldCheck },
  { id: 40001002, parentId: 40000010, name: "Fire Safety", icon: ShieldCheck },
  { id: 40001004, parentId: 40000010, name: "Heat Safety", icon: ShieldCheck },
];

/* ──────────────────────────────────────────────
   3️⃣ KITCHEN PRODUCT VARIANTS (40010101+)
   ────────────────────────────────────────────── */
export const kitchenProducts: CategoryItem[] = [
  /* Pots */
  { id: 40010101, parentId: 40000101, name: "Saucepan Set", icon: Soup },
  { id: 40010103, parentId: 40000101, name: "Stock Pot", icon: Soup },

  /* Pans */
  { id: 40010102, parentId: 40000105, name: "Frying Pan", icon: Soup },

  /* Cookware Sets */
  { id: 40010104, parentId: 40000107, name: "Cookware Set", icon: Soup },

  /* Pressure Cookers */
  { id: 40010201, parentId: 40000102, name: "Electric Pressure Cooker", icon: Flame },
  { id: 40010202, parentId: 40000102, name: "Stovetop Pressure Cooker", icon: Flame },

  /* Baking Trays */
  { id: 40020101, parentId: 40000201, name: "Baking Sheet Set", icon: Flame },

  /* Cake Molds */
  { id: 40020202, parentId: 40000202, name: "Cake Pan", icon: Flame },

  /* Muffin Molds */
  { id: 40020201, parentId: 40000205, name: "Muffin Tray", icon: Flame },

  /* Bakeware Sets */
  { id: 40020301, parentId: 40000206, name: "Bakeware Set", icon: Flame },

  /* Appliances */
  { id: 40030101, parentId: 40000301, name: "Countertop Microwave", icon: Microwave },
  { id: 40030202, parentId: 40000302, name: "Hand Blender", icon: Utensils },
  { id: 40030201, parentId: 40000306, name: "Stand Mixer", icon: Utensils },
  { id: 40030301, parentId: 40000303, name: "Espresso Machine", icon: Coffee },
  { id: 40030302, parentId: 40000307, name: "Electric Kettle", icon: Coffee },
  { id: 40030401, parentId: 40000304, name: "Air Fryer", icon: Flame },

  /* Knives */
  { id: 40040101, parentId: 40000401, name: "Chef Knife", icon: Scissors },
  { id: 40040102, parentId: 40000401, name: "Knife Set", icon: Scissors },

  /* Cooking Utensils */
  { id: 40040201, parentId: 40001101, name: "Spatula Set", icon: Utensils },
  { id: 40040202, parentId: 40001101, name: "Kitchen Utensil Set", icon: Utensils },

  /* Measuring Tools */
  { id: 40040301, parentId: 40000403, name: "Measuring Cups", icon: Scale },
  { id: 40040302, parentId: 40000403, name: "Measuring Spoons", icon: Scale },

  /* Food Storage */
  { id: 40050101, parentId: 40000501, name: "Glass Food Containers", icon: Box },
  { id: 40050102, parentId: 40000501, name: "Food Container Set", icon: Box },
  { id: 40050201, parentId: 40000502, name: "Bento Lunch Box", icon: Box },

  /* Drinkware */
  { id: 40060101, parentId: 40000601, name: "Water Glass Set", icon: GlassWater },
  { id: 40060302, parentId: 40000604, name: "Tumbler Set", icon: GlassWater },
  { id: 40060201, parentId: 40000602, name: "Ceramic Mug", icon: Coffee },
  { id: 40060301, parentId: 40000603, name: "Insulated Water Bottle", icon: GlassWater },

  /* Tableware */
  { id: 40070101, parentId: 40000701, name: "Dinner Plate Set", icon: ShoppingBasket },
  { id: 40070401, parentId: 40000704, name: "Mixing Bowl Set", icon: ShoppingBasket },
  { id: 40070201, parentId: 40000702, name: "Stainless Cutlery Set", icon: Utensils },
  { id: 40070301, parentId: 40000705, name: "Dinnerware Set", icon: ShoppingBasket },

  /* Organization */
  { id: 40080101, parentId: 40000801, name: "Drawer Cutlery Organizer", icon: Box },
  { id: 40080201, parentId: 40000802, name: "Tiered Spice Rack", icon: Box },

  /* Cleaning */
  { id: 40090101, parentId: 40000901, name: "Stainless Dish Rack", icon: Droplets },
  { id: 40090201, parentId: 40000902, name: "Dish Scrubber Set", icon: Droplets },

  /* Safety */
  { id: 40100101, parentId: 40001001, name: "Heat Resistant Gloves", icon: ShieldCheck },
  { id: 40100201, parentId: 40001002, name: "Fire Extinguisher", icon: ShieldCheck },
];